import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useApiClient } from '@/api/client'
import { useWorkspace } from '@/workspaces/WorkspaceContext'

export type Person = {
  id: string
  name: string
  email?: string
  avatarUrl?: string
  userId?: string
  isInvited: boolean
}

type PersonFlat = {
  id: string
  workspace_id: string
  object_type: 'person'
  created_at: string
  updated_at: string
  name: string
  email?: string
  avatar_url?: string
  user_id?: string
}

export function usePeople() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug

  const query = useQuery({
    queryKey: ['people', slug],
    queryFn: async () => {
      const res = await fetchWithAuth<{ items: Array<{ id: string; workspace_id: string; object_type: string; created_at: string; updated_at: string; attributes: Record<string, unknown> }> }>(
        `/api/v1/workspaces/${slug}/records?type=person&limit=500`,
      )
      return res.items.map((item) => ({
        id: item.id,
        workspace_id: item.workspace_id,
        object_type: 'person' as const,
        created_at: item.created_at,
        updated_at: item.updated_at,
        ...(item.attributes as Record<string, unknown>),
      } as PersonFlat))
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  })

  const people = useMemo<Person[]>(() => {
    const raw = query.data ?? []
    const mapped: Person[] = raw.map((flat) => ({
      id: flat.id,
      name: flat.name ?? '',
      email: flat.email,
      avatarUrl: flat.avatar_url,
      userId: flat.user_id,
      isInvited: Boolean(flat.user_id),
    }))

    // Dedupe by userId
    const byUserId = new Map<string, Person>()
    const withoutUserId: Person[] = []
    for (const p of mapped) {
      if (p.userId) {
        if (!byUserId.has(p.userId)) byUserId.set(p.userId, p)
      } else {
        withoutUserId.push(p)
      }
    }
    return [...byUserId.values(), ...withoutUserId]
  }, [query.data])

  const peopleById = useMemo(() => {
    const map = new Map<string, Person>()
    for (const p of people) map.set(p.id, p)
    return map
  }, [people])

  const peopleByUserId = useMemo(() => {
    const map = new Map<string, Person>()
    for (const p of people) {
      if (p.userId) map.set(p.userId, p)
    }
    return map
  }, [people])

  return { people, peopleById, peopleByUserId, isLoading: query.isLoading }
}
